import { Set } from 'immutable';
import React, { useMemo } from 'react';
import { useTransientOccupancyTaxName } from '@/hooks/useTaxName';
import { dayjs } from '@/utils/dayjs';
import { DailyCalendar } from '../daily-calendar';
import { ReviewList } from '../review-list';
import {
  MonthContainer,
  MonthHeader,
  MonthSummary,
  MonthTitle,
} from './styles';
import { SummaryMetric } from './summary-metric';

const MONTH_TITLE_DISPLAY_FORMAT = 'MMMM YYYY';
const NO_DATA_PLACEHOLDER = '--';
const CALENDAR_STATUS_BOOKABLE = 'bookable';
const CALENDAR_STATUS_BOOKED = 'booked';

const AVAILABLE_STATUSES = new Set([
  CALENDAR_STATUS_BOOKABLE,
  CALENDAR_STATUS_BOOKED,
]);

function coalesceNaN(amount, defaultValue = NO_DATA_PLACEHOLDER) {
  if (typeof amount !== 'number' || Number.isNaN(amount)) {
    return defaultValue;
  }
  return amount;
}

const currencyNumberFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
  currencyDisplay: 'narrowSymbol',
  useGrouping: true,
});

function formatCurrency(amount) {
  const displayedAmount = Math.round(coalesceNaN(amount, 0));
  return currencyNumberFormat.format(displayedAmount);
}

export function CalendarMonth({ style, index, data } = {}) {
  const months = data?.months || [];
  const month = months[index];
  const {
    listingLiveEventsByMonth,
    totPaymentsByMonthStartDate,
    showTOTCollectionMetrics,
  } = data || {};

  const totPaymentSummary = totPaymentsByMonthStartDate?.[month?.date];

  const emptyLiveEvents = useMemo(() => {
    const nonEmptyLiveEvents = Object.values(listingLiveEventsByMonth).filter(
      (events) => Boolean(events?.length)
    )[0];
    return nonEmptyLiveEvents?.map(({ events: _, ...rest }) => ({
      ...rest,
      events: [],
    }));
  }, [listingLiveEventsByMonth]);

  const listingLiveEvents =
    listingLiveEventsByMonth?.[month?.date] || emptyLiveEvents || [];

  const { estimateTot, booked, reviews, calendarActivity } = month || {};

  const totAlias = useTransientOccupancyTaxName();

  const numDaysAvailable = calendarActivity?.filter(({ status }) =>
    AVAILABLE_STATUSES.has(status)
  ).length;

  const numDaysBooked =
    booked?.value ??
    calendarActivity?.filter(({ status }) => status === CALENDAR_STATUS_BOOKED)
      .length;

  const displayMonthTitle = dayjs(month?.date).format(
    MONTH_TITLE_DISPLAY_FORMAT
  );

  const isFirstMonthInCalendar = index === 0;
  const isLastMonthInCalendar = index === months.length - 1;

  return (
    <MonthContainer style={style}>
      <MonthHeader
        suppressLeftBorder={isFirstMonthInCalendar}
        suppressRightBorder={isLastMonthInCalendar}
      >
        <MonthTitle>{displayMonthTitle}</MonthTitle>
        <MonthSummary
          alignLeft={!showTOTCollectionMetrics}
          compactLayout={showTOTCollectionMetrics}
        >
          <SummaryMetric
            value={coalesceNaN(numDaysBooked)}
            label={'Nights Booked*'}
          />
          <SummaryMetric
            value={coalesceNaN(numDaysAvailable)}
            label={'Nights Available*'}
          />
          <SummaryMetric
            value={formatCurrency(estimateTot?.value)}
            label={`${totAlias} Payable*`}
          />
          {showTOTCollectionMetrics && (
            <>
              <SummaryMetric
                value={coalesceNaN(totPaymentSummary?.numDaysOccupied)}
                label={'Nights Reported'}
              />
              <SummaryMetric
                value={formatCurrency(totPaymentSummary?.actualTot)}
                label={`${totAlias} Paid`}
              />
            </>
          )}
        </MonthSummary>
      </MonthHeader>
      <DailyCalendar
        firstDayOfMonth={month.date}
        calendarActivity={calendarActivity}
        listingLiveEvents={listingLiveEvents}
        monthIndex={index}
      />
      <ReviewList reviews={reviews} monthIndex={index} />
    </MonthContainer>
  );
}
